export type BadgeShape = 'pill' | 'circle' | 'rounded-square' | 'none';

export type FontFamily = 'Inter' | 'RobotoSlab' | 'Lora' | 'WorkSans';

export const FONT_FAMILY_LABEL: Record<FontFamily, string> = {
  Inter: 'Inter',
  RobotoSlab: 'Roboto Slab',
  Lora: 'Lora',
  WorkSans: 'Work Sans',
};

export const FONT_FAMILIES: FontFamily[] = ['Inter', 'RobotoSlab', 'Lora', 'WorkSans'];

export type FilterOp =
  | 'eq' | 'ne' | 'in' | 'not_in'
  | 'contains' | 'not_contains' | 'starts_with' | 'ends_with'
  | 'is_empty' | 'is_not_empty'
  | 'lt' | 'lte' | 'gt' | 'gte' | 'between'
  | 'regex';

export const FILTER_OPS: FilterOp[] = [
  'eq', 'ne', 'in', 'not_in',
  'contains', 'not_contains', 'starts_with', 'ends_with',
  'is_empty', 'is_not_empty',
  'lt', 'lte', 'gt', 'gte', 'between',
  'regex',
];

/** Field-type → ops applicable to that type. */
export type FieldKind = 'string' | 'number';

export const STRING_OPS: FilterOp[] = [
  'eq', 'ne', 'contains', 'not_contains', 'starts_with', 'ends_with',
  'is_empty', 'is_not_empty', 'in', 'not_in', 'regex',
];

export const NUMBER_OPS: FilterOp[] = [
  'eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'between', 'is_empty', 'is_not_empty',
];

export const OP_LABEL: Record<FilterOp, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  in: 'is one of',
  not_in: 'is none of',
  lt: 'is less than',
  lte: '≤',
  gt: 'is greater than',
  gte: '≥',
  between: 'between',
  regex: 'matches regex',
};

export interface SourceFields {
  from_source: string[];
  custom: string[];
  types: Record<string, FieldKind>;
}

export interface FilterPreviewSample {
  id: string;
  title: string;
}

export interface FilterPreviewResponse {
  matched: number;
  total: number;
  samples: FilterPreviewSample[];
  priority_conflict_count: number | null;
}

export interface SingleClause {
  kind: 'single';
  field: string;
  op: FilterOp;
  value: string;
}

export interface AndGroup {
  kind: 'and';
  clauses: Array<SingleClause | OrGroup>;
}

export interface OrGroup {
  kind: 'or';
  clauses: Array<SingleClause | AndGroup>;
}

export type FilterClause = SingleClause | AndGroup | OrGroup;

export interface ShadowSpec {
  blur: number;
  opacity: number;
  dx: number;
  dy: number;
}

export interface ElementBase {
  visible: boolean;
}
export interface DraggablePosition {
  x: number;
  y: number;
}
export interface LogoElement extends ElementBase, DraggablePosition {
  scale: number;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
}
export interface RatingElement extends ElementBase, DraggablePosition {
  fill: string;
  text_color: string;
  scale: number;
  shape: BadgeShape;
  font_family?: FontFamily;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
}
export interface DealElement extends ElementBase, DraggablePosition {
  fill: string;
  text_color: string;
  scale: number;
  shape: BadgeShape;
  font_family?: FontFamily;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
}
export interface PriceCardElement extends ElementBase, DraggablePosition {
  fill: string;
  text_color: string;
  sale_color: string;
  scale: number;
  font_size: number;
  font_family?: FontFamily;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
  // Per-line styling. Colours and families left unset fall back to the
  // card-wide values, so pre-existing configs render unchanged.
  strike_color?: string;
  regular_color?: string;
  sale_font_family?: FontFamily;
  strike_font_family?: FontFamily;
  strike_font_size?: number;
}

/** Fit box for the product cutout. No visibility toggle — an ad always has
 *  its product. Absent from a config = the compositor's default band. */
export interface ProductImageElement extends Partial<DraggablePosition> {
  scale?: number;
  /** Explicit box (2026-08-31 knobs). Both REPLACE the scaled band's dimensions. */
  w?: number;
  h?: number;
  /** Crop the cutout to its alpha bbox before fitting (no-op under cutout:"none"). */
  trim?: boolean;
  visible?: boolean;
  // no-cutout wave (2026-09-01): per-design override of Store.default_cutout_mode.
  // Absent = follow the store default; set = wins over the store either way.
  cutout?: 'auto' | 'none';
}

/** The implicit band the compositor has always used, in 1080-space.
 *  Mirrors feedforge.compositor.product_image_default_box((1080, 1080)). */
export const PRODUCT_IMAGE_DEFAULT_BOX = { x: 64, y: 140, w: 952, h: 703 } as const;

// Same ratios as feedforge.layout.CUTOUT_*; the band's WIDTH follows the canvas
// width, which is what makes it 476 rather than 952 inside a 540px product half.
const CUTOUT_SIDE_PADDING_RATIO = 0.06;
const CANVAS_W = 1080;

/** The default fit band for a canvas of `canvasW`, mirroring
 *  feedforge.compositor.product_image_default_box. Height is unchanged by the
 *  split — only the width halves. Explicit return type: without it TS infers
 *  y/h as the literal 140/703 (copied from PRODUCT_IMAGE_DEFAULT_BOX's
 *  `as const`), which resolveProductImageBox's destructured `let` bindings
 *  then cannot reassign. */
export function productImageDefaultBox(
  canvasW: number = CANVAS_W,
): { x: number; y: number; w: number; h: number } {
  const sidePad = Math.floor(canvasW * CUTOUT_SIDE_PADDING_RATIO);
  return {
    x: sidePad,
    y: PRODUCT_IMAGE_DEFAULT_BOX.y,
    w: canvasW - sidePad * 2,
    h: PRODUCT_IMAGE_DEFAULT_BOX.h,
  };
}

/** Python int(): truncation toward zero for numbers; bools are ints
 *  (True=1, False=0 — bool is an int subclass); strings must be plain,
 *  optionally-signed integers (int("400.5") RAISES in Python, so such
 *  values are skipped; int("+10") == 10, so a leading "+" is accepted). */
function pyInt(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.trunc(raw) : null;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'string') return /^\s*[+-]?\d+\s*$/.test(raw) ? parseInt(raw, 10) : null;
  return null;
}

/** Resolve the effective fit box, mirroring feedforge.compositor.product_image_box
 *  step for step (pinned by src/lib/__tests__/productBox.parity.test.ts against a
 *  fixture the Python side generates — if a case fails, fix this, never the JSON).
 *
 *  Must stay the single source of truth for the editor: the drag handle and the
 *  X/Y fields both read it, so a config carrying only `scale` reports the
 *  centred box the renderer will actually use rather than a bogus 0/0.
 *
 *  `canvasW` is the width the product composition is rendered at — 1080
 *  normally, the product half in lifestyle mode. Passing the wrong one draws a
 *  handle roughly twice the size of the band that actually renders.
 *
 *  Order is load-bearing: `scale` resizes the default band about its centre;
 *  explicit `w`/`h` then REPLACE the dimensions (re-centred), so `scale` is
 *  inert on any axis carrying an explicit `w`/`h` and stays live on the other
 *  one; `x`/`y` (both keys present) then override the origin outright.
 */
export function resolveProductImageBox(
  el: ProductImageElement | undefined,
  canvasW: number = CANVAS_W,
) {
  const D = productImageDefaultBox(canvasW);
  let { x, y, w, h } = D;
  if (!el || Object.keys(el).length === 0) return { x, y, w, h };
  const bag = el as Record<string, unknown>;

  // float(overrides.get("scale", 1.0) or 1.0), except (TypeError, ValueError)
  // -> 1.0. Python-falsy raw (0/""/null/absent/False) takes the "or 1.0"
  // branch; True dodges that (it's truthy) but float(True) == 1.0 anyway, so
  // both booleans land on 1.0 too. A non-empty string is ALWAYS truthy in
  // Python, even pure whitespace, so it reaches float() — which RAISES on
  // whitespace-only input (unlike JS Number(), which reads "" and " " as 0)
  // — so trim first and treat what's left empty the same as absent.
  let scale = 1;
  const rawScale = bag.scale;
  if (
    rawScale !== undefined && rawScale !== null && rawScale !== '' &&
    rawScale !== 0 && rawScale !== true && rawScale !== false
  ) {
    if (typeof rawScale === 'string') {
      const trimmed = rawScale.trim();
      if (trimmed !== '') {
        const n = Number(trimmed);
        scale = Number.isFinite(n) ? n : 1;
      }
    } else {
      const n = Number(rawScale);
      scale = Number.isFinite(n) ? n : 1;
    }
  }
  if (scale !== 1) {
    const sw = Math.max(1, Math.trunc(w * scale));
    const sh = Math.max(1, Math.trunc(h * scale));
    x += Math.floor((w - sw) / 2);
    y += Math.floor((h - sh) / 2);
    w = sw; h = sh;
  }
  for (const [key, axis] of [['w', 'x'], ['h', 'y']] as const) {
    const raw = bag[key];
    if (raw === undefined || raw === null || raw === '') continue;
    const n = pyInt(raw);
    if (n === null) continue;
    const val = Math.max(1, n);
    if (axis === 'x') { x += Math.floor((w - val) / 2); w = val; }
    else { y += Math.floor((h - val) / 2); h = val; }
  }
  // `"x" in overrides and "y" in overrides` — key presence, then int(). A JSON
  // null here makes the compositor RAISE; the editor never writes null
  // (mergeElement deletes undefined instead), so treat it as absent, same as
  // an unparseable string (the compositor would raise there too).
  if ('x' in bag && 'y' in bag && bag.x != null && bag.y != null) {
    x = pyInt(bag.x) ?? x;
    y = pyInt(bag.y) ?? y;
  }
  return { x, y, w, h };
}
export interface DisclaimerElement extends ElementBase, DraggablePosition {
  color: string;
  size: number;        // tier-1 legacy field, kept for backcompat
  font_size: number;   // tier-2 explicit name
  font_family?: FontFamily;
}

export interface BackgroundEffect {
  enabled: boolean;
  style: 'radial' | 'linear';
  direction: 'vertical' | 'horizontal' | 'diagonal';
  intensity: number; // 0.0–1.0
}

export interface BackgroundElement {
  type: 'image' | 'color';
  color?: string;      // #RRGGBB, required when type === 'color'
  effect: BackgroundEffect;
}

/** Split-canvas lifestyle mode. When enabled AND an image resolves, one half of
 *  the canvas carries a lifestyle photo and the other the product composition.
 *
 *  Mirrors feedforge.lifestyle.LifestyleSpec. Note this element is NOT
 *  draggable and has no `type` field — the compositor's custom-overlay loop
 *  keys off type=text|field, so lifestyle is correctly skipped there. */
export interface LifestyleElement {
  enabled: boolean;
  /** Which side the LIFESTYLE half occupies. */
  side: 'left' | 'right';
  /** The lifestyle half's fraction of the width, 0.30–0.70. */
  ratio: number;
  /** Custom-field label for a per-product photo, or null for "upload an image".
   *  There is no separate `source` discriminator any more — round 1 had one and
   *  it could contradict the upload, so an uploaded image showed nothing. */
  field: string | null;
  /** Bucket-relative object path of an uploaded image. Set = it WINS over the
   *  field, which is why picking a field must clear it. */
  asset: string | null;
  /** Horizontal crop anchor for the cover-crop — which side of a wide photo
   *  survives being cut down to the tall, narrow lifestyle half. */
  focus: 'left' | 'center' | 'right';
}

/** Sentinel value of the single source control meaning "upload an image". */
export const LIFESTYLE_UPLOAD = '__upload__';

/** Which way the photo is being sourced, derived exactly as the backend does
 *  (feedforge.lifestyle.LifestyleSpec.source). */
export function lifestyleSource(el: LifestyleElement | undefined): 'field' | 'static' {
  return el?.asset ? 'static' : 'field';
}

/** Did the server actually split, and if not, why not.
 *
 *  An unresolved photo renders a plain full-canvas ad — pixel-identical to the
 *  mode being off — so the editor CANNOT infer this from the preview image. It
 *  comes back on X-Lifestyle-State / X-Lifestyle-Reason. Round 1 had no such
 *  signal and drew a "LIFESTYLE 50%" chip over unsplit ads. */
export type LifestyleState = 'off' | 'resolved' | 'unresolved';
export type LifestyleReason = 'no_field_value' | 'no_asset' | 'fetch_failed';

/** ui-light: 'standin' = the cutout isn't cached, so the RAW source photo
 *  (background intact) was composed — banner it, never present it as the ad. */
export type CutoutState = 'cached' | 'standin';

export interface PreviewResult {
  blob: Blob;
  state: LifestyleState;
  reason: LifestyleReason | null;
  cutoutState: CutoutState;
}

export const LIFESTYLE_RATIO_MIN = 0.3;
export const LIFESTYLE_RATIO_MAX = 0.7;

export const DEFAULT_LIFESTYLE: LifestyleElement = {
  enabled: true,
  side: 'left',
  ratio: 0.5,
  field: 'lifestyle_image',
  asset: null,
  focus: 'center',
};

/** The element to write when the operator switches lifestyle mode on.
 *
 *  Defaults to the store's first mapped custom field so the very first preview
 *  has a chance of resolving. With nothing mapped, defaulting to a field would
 *  guarantee an unresolved render, so upload mode is the honest starting point. */
export function initialLifestyle(customFields: string[]): LifestyleElement {
  return { ...DEFAULT_LIFESTYLE, field: customFields[0] ?? null };
}

/** Read a config's lifestyle element, or undefined when the key is absent.
 *  Absent and `enabled: false` are both "off" for rendering purposes. */
export function getLifestyle(
  elements: DesignConfig['elements'] | undefined,
): LifestyleElement | undefined {
  const raw = (elements as unknown as Record<string, unknown> | undefined)?.lifestyle;
  if (raw == null || typeof raw !== 'object') return undefined;
  return raw as LifestyleElement;
}

/** True when the canvas should render as a split. */
export function isLifestyleActive(elements: DesignConfig['elements'] | undefined): boolean {
  return getLifestyle(elements)?.enabled === true;
}

/** Product-half width in 1080-space, mirroring feedforge.lifestyle.split_widths.
 *  The lifestyle half is DERIVED BY SUBTRACTION so the two always sum to 1080. */
export function splitWidths(canvasW: number, ratio: number): [number, number] {
  const r = Number.isFinite(ratio)
    ? Math.min(LIFESTYLE_RATIO_MAX, Math.max(LIFESTYLE_RATIO_MIN, ratio))
    : 0.5;
  const productW = Math.max(1, Math.min(canvasW - 1, Math.round(canvasW * (1 - r))));
  return [productW, canvasW - productW];
}

/** The widths the last render actually composed at: the product half and the
 *  lifestyle half, or the whole canvas and 0.
 *
 *  ONE helper because two surfaces need the same answer and drifting apart is
 *  silent: the Canvas drag layer clamps to it and the ElementPanel resolves
 *  AND WRITES the product box against it, so a panel resolving at 1080 while
 *  the render composes at 540 shows a band twice the rendered width and
 *  persists it on the first field edit.
 *
 *  Keys off the RENDER (`lifestyleState`), not the config: an unresolved photo
 *  composes at the full 1080 even though the config says split. */
export function productSplit(
  elements: DesignConfig['elements'] | undefined,
  lifestyleState: LifestyleState,
): [productW: number, lifestyleW: number] {
  if (!isLifestyleActive(elements) || lifestyleState !== 'resolved') return [CANVAS_W, 0];
  return splitWidths(CANVAS_W, getLifestyle(elements)?.ratio ?? 0.5);
}

export interface CustomTextElement extends ElementBase, DraggablePosition {
  type: 'text';
  value: string;
  fill: string;
  text_color: string;
  font_family: FontFamily;
  font_size: number;
  shape: BadgeShape;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
}

export interface CustomFieldElement extends ElementBase, DraggablePosition {
  type: 'field';
  field: string;
  fill: string;
  text_color: string;
  font_family: FontFamily;
  font_size: number;
  shape: BadgeShape;
  shadow?: ShadowSpec | null;
  border_radius?: number | null;
}

export type CustomElement = CustomTextElement | CustomFieldElement;

export type ElementKey =
  | 'logo' | 'rating' | 'deal' | 'price_card' | 'disclaimer' | 'product_image';

export interface DesignConfig {
  schema_version: number;
  name: string;
  priority: number;
  filter: FilterClause | null;
  assets: { background: string; logo: string };
  elements: {
    logo: LogoElement;
    rating: RatingElement;
    deal: DealElement;
    price_card: PriceCardElement;
    disclaimer: DisclaimerElement;
    background?: BackgroundElement;
    product_image?: ProductImageElement;
    lifestyle?: LifestyleElement;
    [k: string]: LogoElement | RatingElement | DealElement | PriceCardElement
      | DisclaimerElement | CustomElement | BackgroundElement | ProductImageElement
      | LifestyleElement | undefined;
  };
  created_at: string;
  updated_at: string;
  active_from?: string | null;   // ISO 8601 UTC; null/undefined = unbounded
  active_to?: string | null;
}

export interface SampleProduct {
  id: string;
  title: string;
  brand: string;
  price: string;
  sale_price: string | null;
  currency: string;
  image_url: string;
  discount_pct: number | null;
  /** Provenance (W-C [UX-5]): 'fixture' = bundled demo product, not real data. */
  source?: 'live' | 'fixture';
  /** ui-light (2026-08-27): the editor is read-only against the cutout cache.
   *  'missing' = never rendered/cached — previews compose the raw photo and
   *  the operator should queue a re-cut. */
  cutout_state?: 'cached' | 'missing';
}

/** Type guard: is this element a custom text/field overlay? */
export function isCustomElement(el: unknown): el is CustomElement {
  if (!el || typeof el !== 'object') return false;
  const t = (el as { type?: unknown }).type;
  return t === 'text' || t === 'field';
}

// --- Identity / auth (multi-tenancy, 2026-06-24) --------------------------

export type Role = 'admin' | 'client';

/** GET /api/me — the SPA shell's source of truth. */
export interface Me {
  uid: string;
  email: string;
  role: Role;
  org: { id: string; name: string } | null;
  org_role: OrgRole;
  group_ids: string[];
  groups: StoreGroup[];
  stores: Store[];
}

/** Admin Users view row (GET /api/admin/users) — never carries a password hash. */
export interface AdminUser {
  id: string;
  email: string;
  role: Role;
  disabled: boolean;
  auth_providers: string[];
  created_at: string;
  org_id?: string | null;
  org_role?: OrgRole;
  group_ids?: string[];
}

export interface CreateUserBody {
  email: string;
  password?: string;   // omit to get a one-time set_password_token instead
  role?: Role;         // default 'client'
  display_name?: string;
  org_id?: string | null;
  org_role?: OrgRole;
  group_ids?: string[];
}

// --- Organizations / access levels (2026-09-02) ----------------------------

export type OrgRole = 'owner' | 'member';

export interface StoreGroup {
  id: string;
  name: string;
}

export interface Organization {
  id: string;
  name: string;
  groups: StoreGroup[];
  created_at: string;
  updated_at: string;
}

export interface OrgSummary extends Organization {
  n_stores: number;
  n_users: number;
}

export interface OrgUser {
  id: string;
  email: string;
  display_name: string | null;
  org_role: OrgRole;
  group_ids: string[];
  disabled: boolean;
  created_at: string;
}

export interface OrgDetail {
  org: Organization;
  stores: Store[];
  users: OrgUser[];
}

export interface CreateUserResult {
  user: { id: string; email: string; role: Role; disabled: boolean };
  set_password_token: string | null;
}

/** Per-store trailing-30d rollup (GET /api/admin/usage). 'unattributed' =
 * runs predating the feedforge_runs.store_id column. */
export interface UsageRow {
  store_id: string;
  n_runs: number;
  renders_30d: number;
  renders_per_day: number;
  last_run_at: string | null;
  last_run_status: string | null;
  est_cost_30d_usd: number;
}

// --- Stores (tier 4a) -----------------------------------------------------

export interface CustomFieldMapping {
  label: string;
  bq_column: string;
}

export interface StoreSource {
  type: 'feed' | 'bq';
  feed_url?: string | null;
  bq_table?: string | null;
  bq_column_map?: Record<string, string> | null;
  bq_custom_fields?: CustomFieldMapping[];
}

export interface Store {
  id: string;
  name: string;
  source: StoreSource;
  default_assets: Record<string, string>;
  default_disclaimer: string | null;
  sample_product_ids: string[];
  created_at: string;
  updated_at: string;
  // Feed output (spec 2026-09-03 §5) — republished product feed with images swapped.
  feed_output?: FeedOutput;
  // Wave 8 (delete-store reliability) additions
  deletion_status?: 'in_progress' | 'failed' | null;
  deletion_error?: string | null;
  // Multi-tenancy additions
  owner_id?: string | null;
  member_ids?: string[];
  // Access levels (2026-09-02): the tenant boundary + optional group
  org_id?: string | null;
  group_id?: string | null;
  // salessmash (2026-08-28): render schedule frequency, priced per tier
  render_cadence?: RenderCadence;
  // Phase 2a Task 4: per-store spend gates (admin-set; null = ungated)
  max_products?: number | null;
  max_run_usd?: number | null;
  // no-cutout + used-inputs wave (2026-09-01): store-level default cutout
  // behaviour. A design's own product_image.cutout still wins either way
  // (spec §1.1) — this only applies when a design leaves the key unset.
  default_cutout_mode?: 'auto' | 'none';
  // render_hash payload schema (spec §2.1): 3 = legacy, 4 = used-inputs
  // gating. Flipping re-renders the whole store once on the next run.
  hash_schema?: 3 | 4;
}

export type RenderCadence = 'daily' | '2x-day' | '3x-day' | 'hourly';

export type FeedRefreshMinutes = 60 | 240 | 480 | 1440;

/** Feed output settings (spec 2026-09-03 §5). path/source_url null = server default. */
export interface FeedOutput {
  enabled: boolean;
  path: string | null;
  source_url: string | null;
  image_tag: string;
  missing_render: 'drop' | 'keep_source';
  additional_images: 'keep' | 'drop';
  refresh_minutes: FeedRefreshMinutes;
  // Product gate addendum (2026-09-04 §product selection): which renders qualify
  // for the feed. 'not_failed' (default) counts unreviewed renders as usable;
  // 'qa_pass'/'human_pass' progressively tighten to reviewed-only.
  render_gate: 'not_failed' | 'qa_pass' | 'human_pass';
  // Review-label filters, same include/hide semantics as LabelFilter (never both
  // for the same label). Sourced from `<store>_review_labels`, a UI-only lens —
  // these lists are the one sanctioned pipeline read of it (feed_orchestrator.py).
  require_labels: string[];
  exclude_labels: string[];
}

export const DEFAULT_FEED_OUTPUT: FeedOutput = {
  enabled: false, path: null, source_url: null, image_tag: 'image_link',
  missing_render: 'drop', additional_images: 'keep', refresh_minutes: 60,
  render_gate: 'not_failed', require_labels: [], exclude_labels: [],
};

export interface FeedMeta {
  path: string; public_url: string; fingerprint: string;
  last_published_at: string; last_checked_at: string;
  items_in: number; items_out: number; items_swapped: number; items_dropped: number;
  items_kept_unbranded: number; bytes_written: number; run_id: string; trigger: string;
}

export interface FeedRunRow {
  run_id: string; trigger: string; started_at: string; ended_at: string | null;
  status: string; refusal: string | null; error: string | null; warning: string | null;
  items_in: number | null; items_out: number | null; items_swapped: number | null;
  items_dropped: number | null; forced?: boolean | null;
}

export interface FeedStatus {
  enabled: boolean; path: string; public_url: string; published_path: string | null;
  image_map_table: string; meta: FeedMeta | null; runs: FeedRunRow[];
  // Product gate addendum (2026-09-04): preview of how many current renders qualify
  // under the store's SAVED gate + label settings. null in dry-run and on query failure
  // (the status card must still render — see feeds.py::_qualifying).
  qualifying: { qualifying: number; current_renders: number } | null;
  // Store's review labels with counts, for the LabelFilterMenu. [] in dry-run.
  labels: ReviewLabel[];
}

export interface FeedRunResult {
  status: 'published' | 'unchanged' | 'refused' | 'error' | 'locked' | 'skipped';
  run_id: string; path: string; public_url: string;
  refusal: string | null; error: string | null; warning: string | null;
  stats: Record<string, number | string | null>; fingerprint: string | null;
}

export interface DeletePreview {
  n_configs: number;
  n_assets: number;
  n_cutouts_exclusive: number;
  n_renders_exclusive: number;
  n_catalog_rows: number;
  n_override_rows: number;
  n_approval_rows: number;
  // False = a scan/count query failed server-side; the counts above are
  // unreliable and must not be trusted before the irreversible cascade (REL-B-3).
  // Optional for backward compatibility with older backends.
  counts_available?: boolean;
}

// --- Wave 2: store create + sample render ---------------------------------

export interface CreateStoreBody {
  id: string;
  name: string;
  source: StoreSource;
  default_assets: Record<string, string>;
  default_disclaimer: string | null;
  sample_product_ids: string[];
  render_cadence?: RenderCadence;
  default_cutout_mode?: 'auto' | 'none';
  org_id?: string | null;
  group_id?: string | null;
  // Feed output (spec 2026-09-03 §5); omitted → server default for feed sources.
  feed_output?: FeedOutput;
}

export interface SampleRenderResultRow {
  product_id: string;
  title: string;
  url: string | null;
  error: string | null;
}

export interface SampleRenderResponse {
  config_hash: string;
  wall_seconds: number;
  results: SampleRenderResultRow[];
}

// --- Cutouts (Wave 12) ----------------------------------------------------

// Mirrors the approved_tier Literal in routes/cutouts.py + feedforge/cutout_cache.py Tier — keep in sync.
// 'none' (2026-09-01): the no-cutout render mode — the original photo was pasted
// with the cutout machinery skipped entirely, not just a cheap/degenerate tier.
export type CutoutTier =
  | 'tier_0' | 'tier_1' | 'tier_2' | 'ben2' | 'tier_sam' | 'manual_review' | 'snapedit' | 'passthrough' | 'floodfill'
  | 'none';

export type OverrideReason =
  | 'class_a_lost_component'
  | 'class_b_wrong_image'
  | 'class_c_edge_speckle'
  | 'manual';

export interface OverrideRow {
  product_id: string;
  reported_at: string;
  reported_by: string | null;
  reason: OverrideReason | null;
  force_tier: CutoutTier;
  resolved_at: string | null;
  notes: string | null;
}

export interface RecordOverridesBody {
  product_ids: string[];
  reason: OverrideReason;
  notes?: string;
}

export interface RecordApprovalBody {
  product_id: string;
  approved_image_url: string;
  approved_tier: CutoutTier;
  notes?: string;
}

// --- Conflict graph (timeline) -------------------------------------------

export interface ConflictEdge {
  winner: string;
  loser: string;
  n_products: number;
  total: number;
}

export interface DeletionStatus {
  store_id: string;
  status: 'in_progress' | 'failed' | 'completed';
  started_at: string;
  last_phase_at: string;
  current_phase: string | null;
  urls_total: number;
  urls_done: number;
  error: string | null;
  completed_at: string | null;
}

// --- Wave 10: Needs-review queue + bulk actions ------------------------

// Tiers a render can be approved at. tier_0/1/2 from the escalation ladder plus
// floodfill/passthrough from the router (router-v1-2026-06-02).
export type NeedsReviewTier =
  | 'tier_0' | 'tier_1' | 'tier_2' | 'ben2' | 'floodfill' | 'passthrough';

export type QueueResolution = 'pending' | 'approved' | 'rejected' | 'all';

export interface NeedsReviewRow {
  product_id: string;
  title: string | null;
  image_link: string;
  cutout_source_image_url: string | null;
  cutout_quality_score: number | null;
  cutout_quality_reason: string | null;
  tier_used: NeedsReviewTier;
  image_status?: string | null;  // 'ok' | 'needs_review' | 'cache_hit' | etc.
  reported_at: string | null;
  reason: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  approved_at: string | null;
  is_approved: boolean;
  is_rejected: boolean;
}

export interface QueueResponse {
  rows: NeedsReviewRow[];
  counts: Record<QueueResolution, number>;
}

export interface BulkApprovalItem {
  product_id: string;
  approved_image_url: string;
  approved_tier: NeedsReviewTier;
}

export interface BulkApprovalsBody {
  items: BulkApprovalItem[];
  notes?: string;
}

export interface BulkActionResult {
  ok_count: number;
  failed: { product_id: string; error: string }[];
}

/** Derived from NeedsReviewRow's is_approved / is_rejected flags. */
export function deriveResolution(row: NeedsReviewRow): QueueResolution {
  if (row.is_approved) return 'approved';
  if (row.is_rejected) return 'rejected';
  return 'pending';
}

// --- Wave 11: SAM interactive segmentation -------------------------------

export interface Click {
  x: number;
  y: number;
  label: 'include' | 'exclude';
}

export type Point = [number, number];

// --- Wave 12: All-renders gallery + lookup -----------------------------

export interface RenderRow {
  product_id: string;
  title: string | null;
  image_link: string | null;
  cutout_tier: CutoutTier | null;
  cutout_quality_score: number | null;
  rendered_at: string | null;
  status: string | null;
  is_approved: boolean;
  approved_by?: string | null;
  // 2026-06-16: cutout-keyed AI QA grade for this product's current cutout.
  is_graded: boolean;
  qa_verdict?: string | null;   // 'pass' | 'fail' | null (ungraded)
}

export type ReprocessMethod =
  | 'tier_0' | 'tier_1' | 'tier_2' | 'ben2' | 'floodfill' | 'passthrough' | 'snapedit';

export interface QueueCount { count: number; }
export interface QueueRunResult {
  execution: string;
  /** ui-light: override rows pending when the run was triggered. */
  queued?: number;
  scope?: 'full' | 'overrides';
}

export type JobExecutionStatus = 'running' | 'succeeded' | 'failed';

/** W-G [CO-8]: upper-bound cost of the next full Job execution. */
export interface RunEstimate {
  n_products: number;
  unit_cost_usd: number;
  max_cost_usd: number;
  note: string;
}

export interface ListRendersResponse {
  rows: RenderRow[];
  total: number;
  has_more: boolean;
  // 2026-06-16: catalog split under the active filters BEFORE the graded toggle.
  base_total: number;
  graded_total: number;
}

export interface ListRendersParams {
  limit?: number;
  offset?: number;
  tier?: CutoutTier;
  max_score?: number;
  since?: string;
  sort?: 'rendered_at_desc' | 'rendered_at_asc' | 'score_asc' | 'score_desc' | 'product_id_asc';
  status?: 'ok' | 'cache_hit' | 'needs_review' | 'any';
  is_approved?: boolean;
  graded?: boolean;   // true = AI-graded only, false = ungraded only, undefined = all
  search?: string;
}

export interface BulkRerenderResponse {
  count: number;
}

// --- QA review -----------------------------------------------------------

export interface QaPendingItem {
  product_id: string;
  title: string | null;
  source_image_url: string;
  ad_image_url: string;
  cutout_image_url: string | null;
}
export interface QaPendingResponse { rubric: string; items: QaPendingItem[]; }
export interface QaVerdict { product_id: string; verdict: 'pass' | 'fail'; reason?: string; }
export interface QaVerdictResult {
  written: number; passed: number; failed: number; remediation_enqueued?: number;
  // 2026-06-28 reliability: the server resolves qa_target via a JOIN to the
  // current view and can write FEWER rows than requested (a product absent from
  // the current catalog is silently dropped). The client must trust `written`,
  // not the 200 status, before hiding an item as "done".
  requested?: number; dropped?: number;
}

export type QaVerdictValue = 'pass' | 'fail';
export type QaReviewFilter =
  | 'needs-fix' | 'queued' | 'disapproved' | 'approved'
  | 'ai-approved' | 'human-approved' | 'overridden'
  | 'manual' | 'recut' | 'unverified' | 'all';
export type QaSuggestedAction = 'escalate' | 'manual' | 'third_party';

/** Re-cut batch state: 'queued' runs on the next job, 'done' awaits re-review. */
export type RecutState = 'queued' | 'done';

/** Buckets defined by carrying a human verdict — "hide reviewed" can only ever
 *  empty them, so the gallery forces the flag off (and disables it) here. */
export const HUMAN_VERDICT_FILTERS: QaReviewFilter[] = [
  'human-approved', 'overridden', 'manual', 'recut',
];

/** Sentinel filter value: products carrying no label at all. Must match
 *  feedforge_ui/review_labels.py::UNLABELLED. */
export const UNLABELLED = '__unlabelled__';

export interface ReviewLabel {
  label: string;
  count: number;
}

/** The label filter: show anything carrying ANY of `include`, then subtract everything
 *  carrying ANY of `exclude` — "tagged pass-1 but not done". Either list may hold the
 *  UNLABELLED sentinel. A label is in at most one of them; LabelFilterMenu owns that
 *  invariant, so no caller can build "show X and hide X". */
export interface LabelFilter {
  include: string[];
  exclude: string[];
}

export const emptyLabelFilter = (): LabelFilter => ({ include: [], exclude: [] });
export const isLabelFilterEmpty = (f: LabelFilter): boolean =>
  f.include.length === 0 && f.exclude.length === 0;

export interface BulkRecutResult {
  recut_count: number;
  diverted_count: number;
  diverted_ids: string[];
  failed: { product_id: string; error: string }[];
}

export interface QaReviewedItem {
  product_id: string;
  title: string | null;
  ad_image_url: string;
  /** Render's rendered_at (epoch seconds) — cache-buster for ad_image_url. */
  render_at?: number | null;
  source_image_url: string;
  cutout_image_url: string | null;
  cutout_tier?: string | null;
  cutout_quality_score?: number | null;
  cutout_quality_reason?: string | null;
  suggested_action?: QaSuggestedAction | null;
  suggested_tier?: string | null;
  ai_verdict: QaVerdictValue | null;
  ai_reason: string | null;
  ai_at: string | null;
  human_verdict: QaVerdictValue | null;
  human_reason: string | null;
  human_at: string | null;
  effective_verdict: QaVerdictValue | null;
  remediation_status: string | null;
  /** Operator-pinned cutout (Run-now/SAM approval) — shown on Approved cards. */
  approved_image_url?: string | null;
  approved_tier?: string | null;
  /** When the pin was recorded (ISO). A pin only stands in for the ad while the
   *  render is OLDER than it; see pinSupersedesRender in QaReviewCard. */
  approved_at?: string | null;
  /** Set when the product belongs to a re-cut batch (any resolution state). */
  recut_state?: RecutState | null;
  /** Reviewer-progress markers. No pipeline effect. */
  labels?: string[];
  /** Commercial value (SEK) driving the value-weighted sort. */
  sales_30d?: number | null;
  stock_value?: number | null;
  /** Catalog-wide importance percentile (0..1): 0.7·sales + 0.3·stock. */
  importance_score?: number | null;
  /** Keyset cursor for the active value sort — the client pages forward on it. */
  sort_rank?: number | null;
}
export interface QaReviewCounts {
  'needs-fix'?: number;
  queued?: number;
  'ai-approved'?: number;
  'human-approved'?: number;
  manual?: number;
  recut?: number;
  graded?: number;
  unverified?: number;
  disapproved: number; approved: number; overridden: number; all: number;
}
export interface QaReviewedResponse { items: QaReviewedItem[]; counts: QaReviewCounts; }

// --- Diagnose card (per-product source-vs-render diagnostic) ---

export type DiagnosticVerdict =
  | 'current'
  | 'stale_price'
  | 'stale_image'
  | 'not_rendered'
  | 'not_in_source';

export interface DiagnosticSource {
  id: string;
  title: string | null;
  brand: string | null;
  price: string | null;
  sale_price: string | null;
  currency: string | null;
  image_url: string;
  link: string | null;
  custom_fields: Record<string, string>;
}

export interface DiagnosticRender {
  render_hash: string;
  adjusted_image_link: string;
  price_sek: string | null;
  sale_price_sek: string | null;
  rendered_at: string | null;
  status: string;
  cutout_tier: string | null;
  cutout_quality_score: string | null;
  cutout_source_image_url: string | null;
  config_name: string | null;
  template_version: string | null;
  title: string | null;
}

export interface DiagnosticCutout {
  kind: 'derived' | 'pinned';
  tier: string | null;
  url: string | null;
  exists: boolean | null;
}

export interface DiagnosticState {
  pinned: boolean;
  approved_tier: string | null;
  approved_at: string | null;
  override_tier: string | null;
  override_reason: string | null;
  qa_verdict: string | null;
  qa_reviewer: string | null;
}

export interface ProductDiagnostic {
  product_id: string;
  verdict: DiagnosticVerdict;
  differences: string[];
  source: DiagnosticSource | null;
  render: DiagnosticRender | null;
  cutout: DiagnosticCutout | null;
  state: DiagnosticState;
}

export interface RerunStats {
  rendered: number;
  cache_hit: number;
  pointer_repaired: number;
  needs_review: number;
  skipped: number;
  error: number;
}

export interface RerunResult {
  stats: RerunStats;
  diagnostic: ProductDiagnostic;
}
